import type { SmartSearchFeatureHit } from '../types'

import type { LayerSearchRow } from './buildLayerSearchRows'
import type { SmartSearchLayerGroup } from './buildUnifiedSearchGroups'
import type { LayerSearchFacetFilters } from './filterLayerSearchIndex'
import { SMART_SEARCH_MAX_RESULTS } from './filterSearchIndex'
import { folderFacetMatches } from './folderFacetMatch'
import { matchesAttributeHaystack } from './matchAttributeHaystack'
import { hasPositiveTerms, parseAttributeQuery } from './parseAttributeQuery'
import {
  serverHitToFeatureHit,
  type ServerSearchHit,
} from './serverHitToFeatureHit'

/**
 * Build unified layer groups by combining:
 * - Server feature hits (from /search endpoint) grouped by layer_id
 * - Client-side layer-name matches (haystack match on layerRows)
 *
 * Browse mode (no positive terms): all eligible layers with empty matchedFeatures.
 * Search mode: layers with server feature hits + layers matching by name (empty features).
 */
export type BuildGroupsExtraFilters = {
  creatorId?: number | null
}

export function buildGroupsFromServerHits(
  query: string,
  serverHits: ServerSearchHit[],
  layerRows: LayerSearchRow[],
  facets: LayerSearchFacetFilters,
  extra?: BuildGroupsExtraFilters,
): SmartSearchLayerGroup[] {
  const parsed = parseAttributeQuery(query)
  const requireText = hasPositiveTerms(parsed)

  const filteredLayers = layerRows.filter(lr => {
    if (!folderFacetMatches(lr.category, facets.category)) return false
    if (extra?.creatorId != null) {
      if (lr.layerCreatorUserId !== extra.creatorId) return false
    }
    return true
  })

  const layerRowMap = new Map<number, LayerSearchRow>()
  for (const lr of filteredLayers) {
    layerRowMap.set(lr.layerId, lr)
  }

  // Group server hits by layer_id, keeping only those in eligible layers
  const matchedByLayer = new Map<number, SmartSearchFeatureHit[]>()

  if (requireText) {
    for (let i = 0; i < serverHits.length; i++) {
      const hit = serverHits[i]
      if (!layerRowMap.has(hit.layer_id)) continue
      const lr = layerRowMap.get(hit.layer_id)
      const arr = matchedByLayer.get(hit.layer_id) ?? []
      arr.push(serverHitToFeatureHit(hit, lr, `srv-${hit.layer_id}-${i}`))
      matchedByLayer.set(hit.layer_id, arr)
    }

    // Also include layers where the layer NAME/haystack matches the query
    // (even if no feature hits from the server)
    for (const lr of filteredLayers) {
      if (!matchesAttributeHaystack(lr.haystack, parsed)) continue
      if (!matchedByLayer.has(lr.layerId)) {
        matchedByLayer.set(lr.layerId, [])
      }
    }
  } else {
    // Browse mode: all eligible layers with featureCount > 0
    for (const lr of filteredLayers) {
      if (lr.featureCount > 0) {
        matchedByLayer.set(lr.layerId, [])
      }
    }
  }

  const groups: SmartSearchLayerGroup[] = []
  const MAX_GROUPS = SMART_SEARCH_MAX_RESULTS

  for (const lr of filteredLayers) {
    if (groups.length >= MAX_GROUPS) break
    const mf = matchedByLayer.get(lr.layerId)
    if (mf === undefined) continue

    groups.push({
      layerId: lr.layerId,
      layerName: lr.layerName,
      sharingKind: lr.sharingKind,
      category: lr.category,
      layerFlyFeature: lr.flyFeature,
      layerCreatorUserId: lr.layerCreatorUserId ?? null,
      layerCreatorUsername: lr.layerCreatorUsername ?? null,
      totalFeaturesInLayer: lr.featureCount,
      matchedFeatures: mf,
    })
  }

  groups.sort((a, b) =>
    a.layerName.localeCompare(b.layerName, 'fr', { sensitivity: 'base' }),
  )

  return groups
}
